import { useState } from 'react'
import { Input, Badge, Tag, Empty } from 'antd'
import { SearchOutlined, ClockCircleOutlined, CheckCircleOutlined } from '@ant-design/icons'
import type { BiddingCase } from '../../types/index'

interface CaseListPanelProps {
  cases: BiddingCase[]
  selectedCaseId?: string
  onSelectCase: (caseData: BiddingCase) => void
}

const CaseListPanel = ({ cases, selectedCaseId, onSelectCase }: CaseListPanelProps) => {
  const [searchText, setSearchText] = useState('')
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('all')

  const filteredCases = cases.filter(caseData => {
    const matchSearch = caseData.projectName.toLowerCase().includes(searchText.toLowerCase()) ||
                       caseData.caseId.toLowerCase().includes(searchText.toLowerCase())
    const matchFilter = filter === 'all' || 
                       (filter === 'pending' && caseData.status === 'annotating') ||
                       (filter === 'completed' && caseData.status === 'completed')
    return matchSearch && matchFilter
  })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'annotating':
        return <Badge status="processing" text="标注中" />
      case 'reviewing':
        return <Badge status="warning" text="审核中" />
      case 'completed':
        return <Badge status="success" text="已完成" />
      default:
        return <Badge status="default" text="待处理" />
    }
  }

  return (
    <div className="annotation-panel">
      <div className="annotation-panel-header">
        <span>📁 案件清单</span>
        <span className="text-xs text-gray-400">({filteredCases.length})</span>
      </div>
      
      <div className="p-3 border-b">
        <Input
          prefix={<SearchOutlined />}
          placeholder="搜索案件..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
         
        />
        <div className="flex gap-2 mt-2">
          <Tag 
            color={filter === 'all' ? 'blue' : undefined}
            className="cursor-pointer"
            onClick={() => setFilter('all')}
          >
            全部
          </Tag>
          <Tag 
            color={filter === 'pending' ? 'orange' : undefined}
            className="cursor-pointer"
            onClick={() => setFilter('pending')}
          >
            待标注
          </Tag>
          <Tag 
            color={filter === 'completed' ? 'green' : undefined}
            className="cursor-pointer"
            onClick={() => setFilter('completed')}
          >
            已完成
          </Tag>
        </div>
      </div>

      <div className="annotation-panel-content p-0">
        {filteredCases.length === 0 ? (
          <Empty description="暂无案件" className="mt-8" />
        ) : (
          <div className="divide-y">
            {filteredCases.map((caseData) => (
              <div
                key={caseData.caseId}
                className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedCaseId === caseData.caseId ? 'selected-item bg-blue-50' : ''
                }`}
                onClick={() => onSelectCase(caseData)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{caseData.caseId}</span>
                  {getStatusBadge(caseData.status)}
                </div>
                <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                  {caseData.projectName}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>{caseData.createdAt}</span>
                  {caseData.status === 'annotating' && (
                    <ClockCircleOutlined />
                  )}
                  {caseData.status === 'completed' && (
                    <CheckCircleOutlined className="text-green-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CaseListPanel
